/**
 * OrgChangeStepper — v0.14 Wave 3 (org-change feature)
 *
 * Single-modal stepper that drives the end-to-end client-change flow on the
 * invoice page's BILL TO panel. Replaces the super-admin SQL path Christa
 * needed for shapes like her 339/447/567 mess (invoice tagged to the wrong
 * parent client).
 *
 * Spec: meta-bus/operations/projects/buildgravity-platform/v0.14-bill-to/
 *       org-change-edge-cases-v2-2026-05-28.md
 *
 * Architecture per §1.0:
 *   Step 1   — Pick or create client (picker + always-visible "+ Create" CTA)
 *   Step 1.5 — Inline create-client form (pre-filled with picker search value)
 *   Step 2   — DBA-match dual-path sub-modal (LOCKED VERBATIM copy §14.0)
 *   Step 3   — Confirmation with cascade preview
 *
 * Single morphing modal — no stacked dialogs. Breadcrumb top-of-modal shows
 * the current step + completed steps. Back is non-destructive (§5.14 — search,
 * selection, DBA picker choice all retained when navigating back).
 *
 * Vocabulary lock per §0.2:
 *   - "Client" in UI, never "Organization"
 *   - "Billing track" instead of "scope"
 *   - "DBA" instead of "establishment"
 *   - Contact-scope tiers: "Just this invoice" / "For this DBA" / "For the whole client"
 *
 * Backend contract (Wave 2 owns, Wave 3 calls):
 *   - PUT /api/invoices/{id} with { organization_id, expected_organization_id,
 *     archive_source_dba?, ... } → response includes org_change_result block.
 *   - POST /api/organizations → 409 with existing_organization_id on dup.
 *   - GET /api/organizations/search-clients → §5.0 unified org+DBA results.
 *
 * Wave 3 ships the UI against the spec'd contract. If Wave 2's real backend
 * lands with diverged shapes, edit the schemas in lib/schemas.ts — the stepper
 * passes the data shape through (no explicit field-picking) per
 * feedback_mutation_hook_field_drift.
 */
import { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
// fixwave iter2 (2026-05-28): removed ChevronLeft/X imports — the
// stepper-level Cancel + Back footer was retired in favour of the single
// Close X provided by DialogContent (handles cancel-with-confirm via
// onOpenChange) plus per-step Back affordances owned by the sub-components.
import { api } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { useProjectScopeSummary, useOrganization } from '@/hooks';
import type { OrgChangeSearchResult } from '@/lib/schemas';
import { OrgChangePicker } from './OrgChangePicker';
import { OrgChangeCreateForm } from './OrgChangeCreateForm';
import { OrgChangeDualPath } from './OrgChangeDualPath';
import { OrgChangeConfirm } from './OrgChangeConfirm';
import { recordPreviousClient } from './orgChangePreviousClient';
import { InvoiceChangeReview } from './InvoiceChangeReview';
import type { InvoiceChangeIntent } from '@/lib/invoiceSelectiveHistoryContracts';

// ---------- Public types ----------

export type StepperStep = 1 | 1.5 | 2 | 3;

export interface OrgChangeStepperInvoice {
  id: string;
  organization_id?: string | null;
  client_name?: string | null;
  project_id?: string | null;
  project_name?: string | null;
  establishment_id?: number | null;
  establishment_name?: string | null;
  billing_address_source?:
    | 'organization'
    | 'project'
    | 'custom'
    | 'establishment'
    | null;
  billing_address_override?: string | null;
  org_billing_address?: string | null;
  org_address?: string | null;
  project_address?: string | null;
  establishment_address?: string | null;
}

export interface OrgChangeRecipientPreview {
  name: string | null;
  email: string | null;
  /** Scope tag for §0.2 contact-scope copy. Derived by the caller from
   *  recipients_meta (snapshot_dba → "DBA: {dba}", snapshot_org → "client-level
   *  — {client}", typed_in/user_added → "added on this invoice"). */
  scopeTag?: string | null;
}

export interface OrgChangeStepperProps {
  open: boolean;
  onClose: () => void;
  invoice: OrgChangeStepperInvoice;
  /** Snapshot of named recipients about to be cleared, with scope tags per
   *  §0.2. Empty array → §11.3 + §0.0 empty-state — Step 3 omits the
   *  recipients block entirely (the 87% dominant case). */
  recipientsToBeCleared: OrgChangeRecipientPreview[];
  /** Tenant id from auth context — used for organization create. */
  tenantId?: string;
  /** Optional callback so the BillToPanel can auto-default the DBA picker
   *  to the DBA Christa originally searched on (§5.13 carry-through). Called
   *  with the establishment_id of the searched DBA when she picked a DBA
   *  result + chose Option 1 ("Bill under parent"). */
  onCarryThroughDBA?: (establishmentId: number, establishmentName: string) => void;
  /** R2 AC1: when provided and the invoice belongs to a named project, the
   *  first screen offers the named-project payer flow (future invoices) in
   *  plain words, because this stepper changes this invoice only. */
  onProjectPayerFlow?: () => void;
}

// ---------- Component ----------

export function OrgChangeStepper({
  open,
  onClose,
  invoice,
  recipientsToBeCleared,
  onCarryThroughDBA,
  onProjectPayerFlow,
}: OrgChangeStepperProps) {
  const queryClient = useQueryClient();
  const [reviewIntent, setReviewIntent] = useState<InvoiceChangeIntent | null>(null);

  // ----- Stepper state machine -----
  const [step, setStep] = useState<StepperStep>(1);
  const [searchValue, setSearchValue] = useState('');
  /** Selection from Step 1 picker (org or DBA result). */
  const [selectedResult, setSelectedResult] = useState<OrgChangeSearchResult | null>(
    null,
  );
  /** Step 2 dual-path choice. 'parent' = Option 1 (recommended).
   *  'standalone' = Option 2 (create standalone client from DBA). */
  const [dualPathChoice, setDualPathChoice] = useState<'parent' | 'standalone' | null>(
    null,
  );
  /** Inline-create form values (Step 1.5). Pre-filled from search or DBA name. */
  const [createName, setCreateName] = useState('');
  const [createLegalName, setCreateLegalName] = useState('');
  /** Result of a successful inline create — used by Step 3 as the resolved
   *  target client when the user came through the create path. */
  const [createdOrg, setCreatedOrg] = useState<{
    id: string;
    name: string;
    legal_name?: string | null;
  } | null>(null);
  /** §14.7 source-DBA archive choice (only when create-standalone path).
   *  Default = true (archive recommended). */
  const archiveSourceDba = false;

  /** Snapshot the invoice's current organization_id at modal-open for the
   *  412 race-protection precondition. Stored once at open; doesn't change as
   *  the user navigates the stepper. */

  /** B2 race-guard (v0.14 fixwave 2026-05-29): synchronous Apply re-entry
   *  guard.
   *
   *  React Query's `isPending` flips after the mutation queues, but React
   *  batches the re-render — so a triple-click on Apply within a single
   *  millisecond fires the click handler 3× before the DOM `disabled`
   *  attribute updates. The 2nd/3rd PUTs land at the server, get 412'd
   *  (organization_mismatch — they race their own preceding PUT), and the
   *  noise pollutes the audit trail.
   *
   *  Per playwright-ux-review.md B2: gate `onClick` synchronously with this
   *  ref. The ref flip happens INSIDE handleApply on the same tick as the
   *  first click, so all subsequent same-tick clicks no-op before they can
   *  reach `updateInvoice.mutate`. The ref is reset on success/error so a
   *  legitimate retry (after a 412 from a real cross-tab race) still works.
   */

  // Reset state on every fresh open (snapshot expected_org_id at the moment
  // of open per §3.1).
  useEffect(() => {
    if (open) {
      setStep(1);
      setSearchValue('');
      setSelectedResult(null);
      setDualPathChoice(null);
      setCreateName('');
      setCreateLegalName('');
      setCreatedOrg(null);
      // B2 race-guard: reset on every fresh open so reopening after a
      // close-without-apply doesn't leave the Apply button gated.
    }
  }, [open, invoice.organization_id]);

  // Long-lived modal state TTL guard (§11.14): if modal idle >5 min, on next
  // interaction refresh the invoice + re-snapshot expected_org_id.
  const lastInteractionAt = useRef<number>(Date.now());
  const markInteraction = useCallback(() => {
    const now = Date.now();
    if (now - lastInteractionAt.current > 5 * 60 * 1000) {
      // Stale — refetch invoice + close stepper gracefully. The next render
      // of the BillToPanel will reflect server truth; the user re-opens fresh.
      toast.info("It's been a while — refreshing this invoice.");
      queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'full'] });
      onClose();
      return false;
    }
    lastInteractionAt.current = now;
    return true;
  }, [invoice.id, onClose, queryClient]);

  // ----- Derived state -----

  /** Final resolved target client for Step 3, regardless of which path got us
   *  here (existing org pick, DBA→parent, DBA→standalone create, greenfield
   *  create). Returns null until enough state is set. */
  const targetClient = useMemo(() => {
    // Created path (greenfield OR DBA→standalone): use createdOrg directly.
    if (createdOrg) {
      return { id: createdOrg.id, name: createdOrg.name };
    }
    if (!selectedResult) return null;
    if (selectedResult.kind === 'org') {
      return { id: String(selectedResult.id), name: selectedResult.name };
    }
    // DBA selected. Step 2 must have run.
    if (dualPathChoice === 'parent' && selectedResult.parent_org_id) {
      return {
        id: selectedResult.parent_org_id,
        name: selectedResult.parent_org_name || '',
      };
    }
    return null;
  }, [selectedResult, dualPathChoice, createdOrg]);

  /** When the user picked a DBA + chose "Bill under parent," the BillToPanel
   *  should auto-default the DBA picker to that DBA post-switch (§5.13).
   *
   *  fixwave iter2 (2026-05-28): R8f-2 BLOCKER — the backend search endpoint
   *  serializes `est.id::text` (string), but the previous guard required
   *  `typeof selectedResult.id === 'number'`, which always evaluated false
   *  against the real wire shape — so carry-through silently dropped to null
   *  on every cascade PUT. Now: tolerate both number and stringified-number
   *  shapes, parse to int, and bail only when the parse fails. */
  const carryThroughDBA = useMemo(() => {
    if (selectedResult?.kind !== 'dba' || dualPathChoice !== 'parent') {
      return null;
    }
    const raw = selectedResult.id;
    const parsed =
      typeof raw === 'number'
        ? raw
        : typeof raw === 'string' && raw.trim() !== '' && Number.isFinite(Number(raw))
          ? Number(raw)
          : null;
    if (parsed === null || !Number.isInteger(parsed)) {
      return null;
    }
    return {
      establishmentId: parsed,
      establishmentName: selectedResult.name,
    };
  }, [selectedResult, dualPathChoice]);

  /** True when we're on the create-standalone path: DBA picked + Step 2
   *  Option 2 + successful create. Drives the §14.7 archive choice. */
  const isCreateStandalonePath =
    selectedResult?.kind === 'dba' && dualPathChoice === 'standalone';

  // ----- Step 3 smart-default scope name (R8e Christa-UX #3 / R8f-2 #N4) -----
  //
  // fixwave iter2 (2026-05-28): SHOULD-FIX — Step 3 still rendered the
  // generic "will either create OR attach" copy even when the target
  // client had an existing scope on this project. Spec §1.2 wants
  // "Will use {client}'s existing track ({DBA}, last invoiced YYYY-MM-DD)."
  //
  // No dedicated pre-flight endpoint exists yet, but the
  // GET /api/projects/{id}/scope-summary route (R5 v0.14) already returns
  // one row per scope on the project with org id + establishment name +
  // last_invoiced_at + sent_invoice_count. We filter to the target org and
  // pick the most-recently-invoiced row, then pass a `scopeAction` prop
  // to OrgChangeConfirm. Confirm already renders the named-track copy when
  // the prop is present (forward-compat landed in fix wave 1) and falls
  // back to the generic copy when null.
  //
  // The hook gates on `enabled: !!projectId`, so we don't fire until we
  // know the project, and React Query caches with staleTime so we don't
  // refetch on every step transition.
  //
  // fixwave iter3 (2026-05-28): R8e-3 adjacent bug #1 — the useMemo
  // previously collapsed loading / error / true-empty into a single
  // "will_create" output, so the ~30ms-2s window before the GET resolved
  // briefly told the user "will create new track" even when an existing
  // scope was about to land. Now: pull `isLoading` + `isError` and return
  // `null` for both — OrgChangeConfirm's generic-fallback branch
  // ("will either create OR attach") renders the truthful-uncertain copy
  // until we actually know.
  const {
    data: scopeSummary,
    isLoading: scopeSummaryLoading,
    isError: scopeSummaryError,
  } = useProjectScopeSummary(invoice.project_id ?? undefined);

  const scopeAction = useMemo(() => {
    if (!targetClient) return null;
    // R8e-3 #1: don't lie during the loading / error window. `null` →
    // OrgChangeConfirm renders the generic "will either create OR attach"
    // copy, which is the honest "we don't know yet" state.
    if (scopeSummaryLoading || scopeSummaryError) return null;
    const scopes = scopeSummary?.scopes ?? [];
    // Find scopes belonging to the target client. There may be multiple
    // (one per DBA + one with NULL DBA), so we pick the most-recently-
    // invoiced row to surface the smart-default chosen by the backend
    // cascade.
    const matching = scopes.filter(
      (s: any) => String(s.organization_id) === String(targetClient.id),
    );
    if (matching.length === 0) {
      return { action: 'will_create' as const };
    }
    const sorted = matching.slice().sort((a: any, b: any) => {
      const dateA = a.last_invoiced_at ? Date.parse(a.last_invoiced_at) : 0;
      const dateB = b.last_invoiced_at ? Date.parse(b.last_invoiced_at) : 0;
      if (dateA !== dateB) return dateB - dateA;
      // Tiebreak by sent_invoice_count.
      return (b.sent_invoice_count ?? 0) - (a.sent_invoice_count ?? 0);
    });
    const top = sorted[0];
    const lastInvoiced = top.last_invoiced_at
      ? String(top.last_invoiced_at).slice(0, 10)
      : null;
    return {
      action: 'found' as const,
      establishment_name: top.establishment_name ?? null,
      last_invoiced_date: lastInvoiced,
      invoice_count_90d: top.sent_invoice_count ?? null,
    };
  }, [scopeSummary, scopeSummaryLoading, scopeSummaryError, targetClient]);

  // ----- Christa-friction-polish F-5 (2026-05-30): target-client address pre-fetch -----
  //
  // When the invoice's billing_address_source = 'organization' (resolves to
  // client billing address), the cascade preview previously showed a blank
  // value + amber warning because the FE had no idea what the target client's
  // billing_address actually was. In practice, if the target client DOES have
  // a billing_address on file, the post-Apply state is fine — the warning is
  // a lie that breeds distrust.
  //
  // Pre-fetch the target client's organization record once we know which
  // client she's switching to. Gated on `enabled: !!targetClient` so we don't
  // fire prematurely. Pass `billing_address` through to OrgChangeConfirm,
  // which uses it to render either (a) the actual resolved address (no
  // warning) or (b) the existing blank+warning state when the target really
  // has no address. Either way: the preview now tells the truth.
  const { data: targetClientFull } = useOrganization(
    targetClient ? targetClient.id : undefined,
  );
  const targetClientBillingAddress =
    targetClientFull?.organization?.billing_address ?? null;

  // ----- Cancel-with-confirm -----

  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  /** Whether any user input has happened — if not, Cancel = close silently
   *  (don't gate a no-op behind a confirm prompt). */
  const hasUserInput =
    !!searchValue ||
    !!selectedResult ||
    !!createdOrg ||
    !!dualPathChoice ||
    !!createName ||
    !!createLegalName;

  const handleCancelClick = () => {
    if (!hasUserInput) {
      onClose();
      return;
    }
    setShowCancelConfirm(true);
  };
  const handleCancelConfirm = () => {
    setShowCancelConfirm(false);
    // §6.4 + R8e Blocker #4 / R8f Finding #9 (fixwave 2026-05-28): if the
    // user inline-created a client during this stepper run and then
    // cancelled, the client persists in the DB (locked decision). Toast so
    // they know the client is in the picker but wasn't applied to this
    // invoice — otherwise they think they cancelled cleanly and the next
    // search surfaces a "ghost" client they don't remember creating.
    if (createdOrg) {
      toast.info(
        `Created "${createdOrg.name}." It's available in the picker but not applied to this invoice.`,
      );
    }
    onClose();
  };

  // ----- Step transitions -----

  const goToStep = (next: StepperStep) => {
    if (!markInteraction()) return;
    setStep(next);
  };

  /** Step 1 → Picker selection. Fork on kind. */
  const handlePickerSelect = (result: OrgChangeSearchResult) => {
    if (!markInteraction()) return;
    setSelectedResult(result);
    // Clear any prior dualPathChoice when reselecting
    setDualPathChoice(null);
    if (result.kind === 'org') {
      // Direct → Step 3.
      goToStep(3);
    } else {
      // DBA → Step 2 dual-path sub-modal.
      goToStep(2);
    }
  };

  /** Step 1 → "+ Create new client" CTA → Step 1.5. */
  const handlePickerCreateNew = () => {
    if (!markInteraction()) return;
    // §6.0: name pre-fills from current search value.
    setCreateName(searchValue.trim());
    setCreateLegalName(searchValue.trim());
    setSelectedResult(null);
    goToStep(1.5);
  };

  /** Step 1.5 → Create button → POST /api/organizations. */
  const handleCreateSubmit = async () => {
    if (!markInteraction()) return;
    if (!createName.trim()) return;
    try {
      const resp = await api.organizations.create({
        name: createName.trim(),
        legal_name: createLegalName.trim() || createName.trim(),
        is_client: true,
        is_self: false,
        is_consultant: false,
        is_government: false,
      } as any);
      setCreatedOrg({
        id: resp.organization.id,
        name: resp.organization.name,
        legal_name: resp.organization.legal_name,
      });
      // Invalidate the org list so picker reflects the new client.
      queryClient.invalidateQueries({ queryKey: ['organizations'] });
      goToStep(3);
    } catch (err: any) {
      // §3.4 + §6.5 + R8f Finding #10 (fixwave 2026-05-28): 409
      // duplicate-name → auto-pick the existing client, toast, continue.
      // Catch the duplicate even when the backend payload omits the
      // existing_organization_id (defensive) so the form doesn't silently
      // get stuck on a console-only error.
      //
      // fixwave iter2 (2026-05-28): R8f-2 BLOCKER #N2 — the previous code
      // read `err.data.code` and `err.data.existing_organization_id`
      // directly, but main.py's exception_handler wraps every HTTPException
      // detail under the `error` key:
      //   { error: { code, message, existing_organization_id, ... },
      //     status_code: 409, ... }
      // So the real paths are `err.data.error.code` /
      // `err.data.error.existing_organization_id` (with `err.data.code`
      // as the legacy/un-wrapped fallback). The previous guards always
      // evaluated false in production → user was stuck on the create form
      // with only the generic backend message in a toast.
      const detail =
        (err?.data && typeof err.data === 'object' && err.data.error) ||
        err?.data ||
        {};
      const code = detail.code;
      const existingId =
        detail.existing_organization_id ?? detail.existingOrganizationId ?? null;
      if (err?.status === 409 && code === 'duplicate_organization_name') {
        const existingName =
          detail.existing_name || createName.trim() || 'this client';
        if (existingId) {
          // Polish wave 2 — B4 (2026-05-30): when the dedup auto-picks an
          // existing client, the previous copy ("Used existing client...
          // instead of creating a duplicate") read as a system-side technical
          // note. Christa needs the message centred on what she meant to do
          // — name the client and tell her she got it. Locked verbatim per
          // sweep B4. Toast lives in `sonner` which defaults to ~4s; that
          // matches the brief's 4-5s requirement without an explicit override.
          toast.info(
            `A client named "${existingName}" already exists — using that one.`,
          );
          setCreatedOrg({ id: existingId, name: existingName });
          queryClient.invalidateQueries({ queryKey: ['organizations'] });
          goToStep(3);
          return;
        }
        // Backend returned 409 without the lookup id — surface a clear
        // error rather than swallowing silently so Christa knows to pick
        // the existing client manually from the picker.
        toast.error(
          `"${existingName}" already exists. Search for it in the picker and pick it instead.`,
        );
        goToStep(1);
        return;
      }
      toast.error(err?.message || 'Failed to create client');
    }
  };

  /** Step 2 → Continue with chosen path. */
  const handleDualPathContinue = () => {
    if (!markInteraction()) return;
    if (!selectedResult || selectedResult.kind !== 'dba') return;
    // Default choice is 'parent' per §14.0 — when user clicks Continue without
    // explicitly clicking the radio, treat as the default (matches the
    // visually-checked state).
    const effective = dualPathChoice ?? 'parent';
    if (effective === 'parent') {
      // Persist the default so downstream state (targetClient memo, etc.)
      // sees it.
      if (dualPathChoice === null) setDualPathChoice('parent');
      // Skip Step 1.5, go straight to Step 3.
      goToStep(3);
    } else if (effective === 'standalone') {
      // Pre-fill the create form with the DBA's name + legal_name and route
      // through Step 1.5.
      setCreateName(selectedResult.name);
      setCreateLegalName(selectedResult.legal_name || selectedResult.name);
      goToStep(1.5);
    }
  };

  /** Step 3 → Apply. Fires PUT /api/invoices/{id} with the resolved org id.
   *
   *  B2 race-guard (v0.14 fixwave 2026-05-29): synchronous re-entry guard
   *  via `applyInFlightRef`. The button's `disabled={isApplying}` prop is
   *  authoritative for screen readers / DOM, but the ref provides the
   *  same-tick gate that prevents triple-click from firing 3 PUTs before
   *  React re-renders. See ref declaration above for the full rationale.
   */
  const handleApply = () => {
    if (!markInteraction() || !targetClient || !invoice.organization_id) return;
    setReviewIntent({
      intent: 'invoice_assignment',
      invoice_id: invoice.id,
      organization_id: invoice.organization_id,
      target_organization_id: targetClient.id,
      target_establishment_id: carryThroughDBA?.establishmentId ?? null,
    });
  };
  // ----- Render -----

  if (!open) return null;

  // Breadcrumb labels per §1.0
  const breadcrumb: Array<{ label: string; isCurrent: boolean; isComplete: boolean }> = [
    {
      label: 'Pick client',
      isCurrent: step === 1 || step === 1.5,
      isComplete: step === 2 || step === 3,
    },
  ];
  if (selectedResult?.kind === 'dba') {
    breadcrumb.push({
      label: 'Choose path',
      isCurrent: step === 2,
      isComplete: step === 3 && !!dualPathChoice,
    });
  }
  breadcrumb.push({
    label: 'Confirm',
    isCurrent: step === 3,
    isComplete: false,
  });

  return (
    <>
      <Dialog
        open={open && reviewIntent === null}
        onOpenChange={(o) => {
          if (!o) handleCancelClick();
        }}
      >
        <DialogContent
          className="max-w-2xl"
          data-testid="org-change-stepper"
          // Close-X in the DialogContent triggers the cancel-confirm flow.
          onEscapeKeyDown={(e) => {
            e.preventDefault();
            handleCancelClick();
          }}
          onPointerDownOutside={(e) => {
            e.preventDefault();
            handleCancelClick();
          }}
        >
          <DialogHeader>
            <DialogTitle>Change the client on this invoice</DialogTitle>
          </DialogHeader>

          {/* Breadcrumb */}
          <nav
            aria-label="Stepper progress"
            className="flex items-center gap-2 text-xs text-slate-500 mb-2"
            data-testid="org-change-breadcrumb"
          >
            {breadcrumb.map((crumb, idx) => (
              <span key={crumb.label} className="inline-flex items-center gap-2">
                {idx > 0 && <span className="text-slate-300">&rsaquo;</span>}
                <span
                  className={
                    crumb.isCurrent
                      ? 'font-semibold text-slate-700'
                      : crumb.isComplete
                        ? 'text-slate-600'
                        : 'text-slate-400'
                  }
                  data-testid={`org-change-crumb-${idx}`}
                >
                  Step {idx + 1}: {crumb.label}
                </span>
              </span>
            ))}
          </nav>

          {/* Step body */}
          {/* R2 AC1: the stepper changes this invoice only. Say so in plain
              words on the first screen, and point the named-project payer
              flow (future invoices) from here when the caller provides it. */}
          {step === 1 && (
            <div
              className="rounded bg-slate-50 border border-slate-200 p-3 space-y-2"
              data-testid="org-change-scope-note"
            >
              <p className="text-sm text-slate-700">
                This changes this invoice only. Future invoices for this project keep their client.
              </p>
              {onProjectPayerFlow && invoice.project_id && (
                <Button
                  type="button"
                  variant="outline"
                  onClick={onProjectPayerFlow}
                  data-testid="org-change-project-payer-button"
                >
                  Change the client for this project
                </Button>
              )}
            </div>
          )}
          {step === 1 && (
            <OrgChangePicker
              searchValue={searchValue}
              onSearchChange={(v) => {
                markInteraction();
                setSearchValue(v);
              }}
              currentOrgId={invoice.organization_id}
              invoiceId={invoice.id}
              onSelect={handlePickerSelect}
              onCreateNew={handlePickerCreateNew}
            />
          )}

          {step === 1.5 && (
            <OrgChangeCreateForm
              name={createName}
              legalName={createLegalName}
              onNameChange={(v) => {
                markInteraction();
                setCreateName(v);
                // Mirror name → legal_name until user diverges
                if (createLegalName === createName) setCreateLegalName(v);
              }}
              onLegalNameChange={(v) => {
                markInteraction();
                setCreateLegalName(v);
              }}
              onSubmit={handleCreateSubmit}
              onBack={() => {
                // §1.0 + §6.3: Back preserves picker search value.
                goToStep(1);
              }}
              isSubmitting={false}
            />
          )}

          {step === 2 && selectedResult?.kind === 'dba' && (
            <OrgChangeDualPath
              dbaName={selectedResult.name}
              parentClientName={selectedResult.parent_org_name || 'the parent client'}
              choice={dualPathChoice}
              onChoiceChange={(c) => {
                markInteraction();
                setDualPathChoice(c);
              }}
              onContinue={handleDualPathContinue}
              onBack={() => {
                // §5.14: Back preserves search + selectedResult so user can
                // reselect or pick the other path.
                goToStep(1);
              }}
            />
          )}

          {step === 3 && targetClient && (
            <OrgChangeConfirm
              invoice={invoice}
              targetClient={targetClient}
              targetClientBillingAddress={targetClientBillingAddress}
              recipientsToBeCleared={recipientsToBeCleared}
              carryThroughDBA={carryThroughDBA}
              scopeAction={scopeAction}
              isCreateStandalonePath={isCreateStandalonePath}
              sourceDbaName={
                isCreateStandalonePath && selectedResult?.kind === 'dba'
                  ? selectedResult.name
                  : null
              }
              sourceDbaParentName={
                isCreateStandalonePath && selectedResult?.kind === 'dba'
                  ? selectedResult.parent_org_name || ''
                  : null
              }
              archiveSourceDba={archiveSourceDba}
              onArchiveChange={() => undefined}
              allowArchiveChoice={false}
              onApply={handleApply}
              onBack={() => {
                // §5.14: Back behavior depends on path
                if (selectedResult?.kind === 'dba') {
                  goToStep(2);
                } else if (createdOrg) {
                  goToStep(1.5);
                } else {
                  goToStep(1);
                }
              }}
              isApplying={reviewIntent !== null}
            />
          )}

          {/* fixwave iter2 (2026-05-28): R8f-2 SHOULD-FIX #N5 — single-
              modal architecture per spec §1.0. Affordance budget:
                - Top-right Close X (Dialog's built-in, routes via
                  onOpenChange → handleCancelClick which gates the
                  cancel-confirm flow when there is user input).
                - One Back at bottom-left of each step body (only when
                  not on Step 1) — owned by the step component.
                - One primary action at bottom-right of each step body
                  (Create / Continue / Apply) — owned by the step
                  component.
              Removed the stepper-level Cancel + Back footer that
              duplicated those affordances (R8f-2 counted 4 on Step 2/3;
              spec demands ≤3 with one Cancel + one Back). Esc and outside
              click also continue to route through handleCancelClick. */}
        </DialogContent>
      </Dialog>

      {reviewIntent && (
        <InvoiceChangeReview
          intent={reviewIntent}
          focusedInvoiceId={invoice.id}
          focusedProjectIds={invoice.project_id ? [invoice.project_id] : []}
          onClose={() => setReviewIntent(null)}
          onApplied={() => {
            queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id, 'full'] });
            queryClient.invalidateQueries({ queryKey: ['invoices', invoice.id] });
            if (invoice.project_id) queryClient.invalidateQueries({ queryKey: ['project-scopes', invoice.project_id] });
            if (carryThroughDBA && onCarryThroughDBA) {
              onCarryThroughDBA(carryThroughDBA.establishmentId, carryThroughDBA.establishmentName);
            }
            if (invoice.organization_id && invoice.client_name) {
              recordPreviousClient(invoice.id, invoice.organization_id, invoice.client_name);
            }
            toast.success(`Client change saved for ${targetClient?.name ?? 'this invoice'}. Download a new PDF to use the updated details. Earlier copies stay unchanged.`);
            setReviewIntent(null);
            onClose();
          }}
        />
      )}

      {/* Cancel-with-confirm dialog. §1.0 copy: "You haven't saved your
          changes — cancel anyway?" */}
      <AlertDialog open={showCancelConfirm} onOpenChange={setShowCancelConfirm}>
        <AlertDialogContent data-testid="org-change-cancel-confirm">
          <AlertDialogHeader>
            <AlertDialogTitle>Abandon changes?</AlertDialogTitle>
            <AlertDialogDescription>
              You haven&apos;t saved your changes — cancel anyway?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="org-change-cancel-keep">
              Keep editing
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleCancelConfirm}
              data-testid="org-change-cancel-confirm-apply"
            >
              Cancel changes
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
